import { Badge } from "@chakra-ui/react";
import { DateTimeFormatter, Instant, LocalDateTime, LocalTime, ZoneId } from "@js-joda/core";
import { AxiosError } from "axios";
import { z } from "zod";
import { Messages, ResponseOf } from "../../core/api";
import { formatErrorResponse } from "../../shared/utils/format-response-error";

// A Router2 POST's request and response types are inlined into the operation
// rather than emitted as named components, so `Messages["...GenerateResponse"]`
// does not exist — reach it through the endpoint instead. (The same is true of
// the neighbouring CheckOverUtilizationResponse.) GET response types *are*
// named, so the context type can be looked up directly.
export type GenerateResponse = ResponseOf<
  "post",
  "./authorizations/:authorizationId/ai_weekly_template_builder/generate"
>;
export type BuilderContext = Messages["AiWeeklyTemplateBuilderContext"];
export type Verification = NonNullable<GenerateResponse["verification"]>;
export type Suggestion = NonNullable<GenerateResponse["suggestion"]>;
export type Violation = Verification["violations"][number];
export type PeriodContext = BuilderContext["periods"][number];
export type SuggestionSummary = BuilderContext["recentSuggestions"][number];

/**
 * One turn of a refinement conversation.
 *
 * Reached through the generate response because a refinement answers with the
 * very same shape — the server treats "revise this" as another way of producing a
 * suggestion, not as a different kind of result.
 */
export type ThreadTurn = GenerateResponse["thread"][number];

/**
 * The refinement endpoint's response.
 *
 * Structurally identical to `GenerateResponse` — the alias exists so the call
 * site reads honestly, not because the two differ.
 */
export type RefineResponse = ResponseOf<
  "post",
  "./authorizations/:authorizationId/ai_weekly_template_builder/suggestions/:suggestionId/refine"
>;

/** Also a POST, so likewise reached through the endpoint rather than by name. */
export type ApplyResponse = ResponseOf<
  "post",
  "./authorizations/:authorizationId/ai_weekly_template_builder/suggestions/:suggestionId/apply"
>;
export type ApplyEligibility = BuilderContext["apply"];
export type ApplyBlocker = ApplyEligibility["blockers"][number];
export type ApplyBlockerCode = ApplyBlocker["code"];

type GenerationStatus = GenerateResponse["status"];
type ViolationSeverity = Violation["severity"];

/**
 * How each blocker should read in the apply bar.
 *
 * `info` reasons are surprising and actionable, and the result panel above says
 * nothing about them, so they get their own alert. `muted` reasons are already
 * spelled out by the big status alert directly above — a second shout would be
 * noise.
 *
 * Mirrors the `STATUS_META` idiom below, and is looked up through
 * `applyBlockerTone` so an unrecognised server code degrades to the server's own
 * prose rather than to `undefined`.
 *
 * Deliberately a total `Record` rather than a `Partial`: the compiler is what
 * tells us a new server-side blocker code needs a decision here.
 */
const APPLY_BLOCKER_TONES: Record<ApplyBlockerCode, "info" | "muted"> = {
  PATIENT_IN_NEW_YORK: "info",
  PATIENT_STATE_UNKNOWN: "info",
  PATIENT_HAS_WEEKLY_TEMPLATE: "info",
  PATIENT_NOT_SERVICEABLE: "info",
  AUTHORIZATION_ALREADY_ENDED: "muted",
  SUGGESTION_ALREADY_APPLIED: "info",
  SUGGESTION_STATUS_NOT_APPLYABLE: "muted",
  SUGGESTION_HAS_NO_SHIFTS: "muted",
  SNAPSHOT_NOT_READABLE: "info",
  // Only ever reached as a POST rejection, via `formatApplyError` — the server
  // raises it after attempting the write, so it never appears in the context's
  // eligibility. Listed because the Record is total, and "info" is right if it
  // ever does surface here.
  NO_VISITS_WOULD_BE_CREATED: "info",
};

export function applyBlockerTone(blocker: ApplyBlocker): "info" | "muted" {
  return APPLY_BLOCKER_TONES[blocker.code] ?? "info";
}

/**
 * "Mon 09:00–13:00 · Wed 09:00–13:00" — the whole pattern in one line, for the
 * confirmation dialog.
 *
 * Goes through `shortWeekday` rather than touching `dayOfWeek` directly: it is
 * typed `string` but is a js-joda object at runtime, so comparing or slicing it
 * quietly misbehaves.
 */
/**
 * Tell the surrounding AngularJS page that visits changed.
 *
 * The weekly template table under this section, and the patient calendar, both
 * refetch on the `refresh_visits` rootScope event — the adapter translates this
 * into that broadcast. Without it the coordinator would be looking at a stale
 * table immediately after a successful create.
 */
export function emitRefreshVisits() {
  window.dispatchEvent(
    new CustomEvent("from-webapp-react", {
      detail: { type: "refreshVisits" },
    })
  );
}

const applyErrorSchema = z.union([
  z.object({
    type: z.literal("NOT_ELIGIBLE"),
    blockers: z.object({ code: z.string(), message: z.string() }).array(),
  }),
  z.object({
    type: z.literal("VISIT_HOURS_RESTRICTION_CONFLICT"),
    conflicts: z.unknown().array(),
  }),
  z.object({ type: z.literal("WEEKLY_TEMPLATE_CONFLICT"), reasons: z.string().array() }),
]);

/**
 * Read the apply endpoint's structured 409 body.
 *
 * The shared `formatErrorResponse` only understands `{error, details}` and would
 * flatten this to a generic message, losing the reason the coordinator needs.
 * Falls back to it for anything that is not one of ours (a network error, a 500).
 */
export function formatApplyError(error: unknown): string {
  const body = (error as AxiosError | undefined)?.response?.data;
  const parsed = applyErrorSchema.safeParse(body);

  if (parsed.success) {
    switch (parsed.data.type) {
      case "NOT_ELIGIBLE":
        return parsed.data.blockers.map((blocker) => blocker.message).join(" ");
      case "WEEKLY_TEMPLATE_CONFLICT":
        return parsed.data.reasons.join(" ");
      case "VISIT_HOURS_RESTRICTION_CONFLICT":
        return (
          "The chosen caregiver would exceed their office's hours restrictions. " +
          "Nothing was created — pick a different caregiver, or leave it unassigned."
        );
    }
  }

  return formatErrorResponse(error);
}

export function formatWeeklyPattern(shifts: Verification["weeklyShifts"]): string {
  return shifts
    .map(
      (shift) =>
        `${shortWeekday(shift.dayOfWeek)} ${formatTime(shift.startTime)}–` +
        `${formatTime(shift.endTime)}`
    )
    .join(" · ");
}

/**
 * Every number on the wire is whole minutes. Dividing by 60 is exact for any
 * 15-minute multiple, so there is nothing to round here — resist the urge to
 * add tolerance.
 */
export function minutesToHours(minutes: number): string {
  return (minutes / 60).toFixed(2);
}

export function formatHours(minutes: number): string {
  return `${minutesToHours(minutes)} h`;
}

export function formatPercent(percent: number | null): string {
  return percent === null ? "—" : `${percent.toFixed(1)}%`;
}

const TIMESTAMP_FORMAT = DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm");

/**
 * e.g. "2026-08-09 23:14", in the viewer's own timezone.
 *
 * Deliberately not the shared `dateFormatter.toDateTime` (which renders
 * "08/09/2026, 11:14 PM"): in a list sorted newest-first, a sortable,
 * unambiguous stamp reads faster than a localised one.
 */
export function formatTimestamp(instant: Instant): string {
  return LocalDateTime.ofInstant(instant, ZoneId.systemDefault()).format(TIMESTAMP_FORMAT);
}

export function formatTime(time: LocalTime): string {
  // js-joda renders LocalTime as "09:00" or "09:00:00"; the seconds are noise here.
  const text = time.toString();
  return text.length > 5 ? text.slice(0, 5) : text;
}

/**
 * Render anything js-joda gives us as a plain string.
 *
 * The API layer revives date/time scalars in place (see `schema-revivers.ts`),
 * so these arrive as js-joda objects and React refuses to render them directly.
 */
export function text(value: { toString(): string }): string {
  return value.toString();
}

export const WEEKDAY_ORDER = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
] as const;

/**
 * The generated types declare `DayOfWeek` as `string`, but `schema-revivers.ts`
 * turns it into a js-joda `DayOfWeek` object at runtime — the format-to-js-joda
 * map in `generate-schema.ts` only covers Instant/LocalDate/LocalDateTime/
 * LocalTime, so the type lies for this one. Normalising through `String()`
 * works for whichever we are actually handed, and is why nothing here indexes
 * into the value directly.
 */
export function weekdayName(dayOfWeek: string): string {
  return String(dayOfWeek);
}

export function shortWeekday(dayOfWeek: string): string {
  const name = weekdayName(dayOfWeek);
  return name.charAt(0) + name.slice(1, 3).toLowerCase();
}

const STATUS_META: Record<
  GenerationStatus,
  { alert: "success" | "warning" | "error" | "info"; title: string; description: string }
> = {
  VERIFIED: {
    alert: "success",
    title: "Verified",
    description: "The scheduling system recomputed this schedule and it stays inside every limit.",
  },
  VERIFIED_WITH_WARNINGS: {
    alert: "warning",
    title: "Verified, with things to check",
    description: "The schedule is within every limit, but some points are worth a look.",
  },
  MATH_MISMATCH: {
    alert: "warning",
    title: "The assistant's arithmetic did not check out",
    description:
      "The schedule itself is within every limit, but the assistant's own totals disagreed with the scheduling system's. The numbers shown below are the verified ones.",
  },
  INVALID: {
    alert: "error",
    title: "This schedule breaks a limit",
    description:
      "The scheduling system found hard errors. Do not use this schedule as-is — see the errors below.",
  },
  LLM_UNAVAILABLE: {
    alert: "error",
    title: "No usable answer from the assistant",
    description: "Nothing was changed. You can try generating again.",
  },
  UNSUPPORTED_AUTHORIZATION: {
    alert: "info",
    title: "Nothing to optimize against",
    description:
      "This authorization has no maximum and no per-period limit, so there is no utilization target to work toward.",
  },
  RATE_LIMITED: {
    alert: "warning",
    title: "Daily limit reached for this patient",
    description: "No generation was run.",
  },
};

export function statusMeta(status: GenerationStatus) {
  return STATUS_META[status];
}

const SEVERITY_COLOR: Record<ViolationSeverity, string> = {
  ERROR: "red",
  WARNING: "orange",
  INFO: "blue",
};

export function SeverityBadge(props: { severity: ViolationSeverity }) {
  return (
    <Badge colorScheme={SEVERITY_COLOR[props.severity]} variant="subtle">
      {props.severity}
    </Badge>
  );
}

export function WithinLimitsBadge(props: { withinLimits: boolean }) {
  return (
    <Badge colorScheme={props.withinLimits ? "green" : "red"} variant="subtle">
      {props.withinLimits ? "OK" : "Over"}
    </Badge>
  );
}
